import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { productPriceInfo } from "../lib/utils";
import SEO from "../lib/SEO";
import SmartImage from "../components/SmartImage";
import CupcakeInquiryForm from "../components/CupcakeInquiryForm";
import RectangularCakeInquiryForm from "../components/RectangularCakeInquiryForm";
import VintageHeartInquiryForm from "../components/VintageHeartInquiryForm";
import { useCart, effectiveUnitPrice } from "../lib/CartContext";

const catSlug = (name) => (name || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Extracts a price add-on from an option label like "Chocolate GF ($5)" → 5.
// Options with no $ amount in parentheses have no surcharge.
const optionSurcharge = (option) => {
    const m = option && option.match(/\(\+?\$(\d+(?:\.\d+)?)\)/);
    return m ? Number(m[1]) : 0;
};

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [qty, setQty] = useState(1);
    const [option, setOption] = useState("");
    const [sizeLabel, setSizeLabel] = useState("");
    const [pickupChecked, setPickupChecked] = useState(false);
    const [activeImage, setActiveImage] = useState(null);
    const touchX = useRef(null);

    useEffect(() => {
        setActiveImage(null);
        setPickupChecked(false);
        api.get(`/products/${slug}`)
            .then((r) => {
                setProduct(r.data);
                if (Array.isArray(r.data.options) && r.data.options.length > 0) {
                    setOption(r.data.options[0]);
                }
                if (Array.isArray(r.data.size_options) && r.data.size_options.length > 0) {
                    setSizeLabel(r.data.size_options[0].label);
                }
            })
            .catch(() => setNotFound(true));
    }, [slug]);

    const allImages = product ? [product.image_url, ...(Array.isArray(product.gallery) ? product.gallery : [])].filter(Boolean) : [];
    const currentImage = activeImage || product?.image_url;
    const currentIdx = Math.max(0, allImages.indexOf(currentImage));
    const step = (dir) => {
        if (allImages.length < 2) return;
        setActiveImage(allImages[(currentIdx + dir + allImages.length) % allImages.length]);
    };

    useEffect(() => {
        if (allImages.length < 2) return undefined;
        const onKey = (e) => {
            if (e.target && ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
            if (e.key === "ArrowLeft") step(-1);
            if (e.key === "ArrowRight") step(1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });

    const hasSizes = Array.isArray(product?.size_options) && product.size_options.length > 0;
    const selectedSize = hasSizes ? product.size_options.find((s) => s.label === sizeLabel) : null;

    const surcharge = optionSurcharge(option);

    const addToCart = () => {
        if (!product) return;
        const nameSuffix = [selectedSize?.label, option].filter(Boolean).join(" — ");
        const basePrice = selectedSize ? selectedSize.price : product.price;
        const item = {
            ...product,
            name: nameSuffix ? `${product.name} — ${nameSuffix}` : product.name,
            price: basePrice + surcharge,
            price_tiers: selectedSize ? [] : product.price_tiers,
            size_label: selectedSize?.label || null,
            option: option || null,
        };
        addItem(item, qty);
        toast.success(`${product.name} added to cart`);
    };

    const buyNow = () => {
        addToCart();
        navigate("/cart");
    };

    if (notFound) {
        return (
            <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center">
                <h2 className="font-display text-3xl mb-4">Product not found</h2>
                <Link to="/shop" className="btn-pink mt-4">Back to shop</Link>
            </main>
        );
    }

    if (!product) {
        return <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center text-ink-soft">Loading…</main>;
    }

    return (
        <main data-testid="product-detail-page">
            <SEO
                title={`${product.name} — Cake Central`}
                description={product.short_description}
                image={product.image_url}
                path={`/shop/product/${product.slug}`}
            />

            <section className="pt-24 md:pt-[10rem] pb-16 bg-[#FFF7FB]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link
                        to={`/shop/${catSlug(product.category)}`}
                        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink-soft hover:text-blush mb-8 transition-colors"
                    >
                        <ArrowLeft size={14} /> Back to {product.category}
                    </Link>

                    <div className="grid md:grid-cols-2 gap-10 md:gap-16">
                        <div>
                            <div
                                className="relative aspect-[4/5] overflow-hidden bg-white"
                                onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
                                onTouchEnd={(e) => {
                                    if (touchX.current == null) return;
                                    const dx = e.changedTouches[0].clientX - touchX.current;
                                    touchX.current = null;
                                    if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
                                }}
                            >
                                <SmartImage src={currentImage} alt={product.name} className="w-full h-full object-cover" />
                                {allImages.length > 1 && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => step(-1)}
                                            aria-label="Previous image"
                                            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-ink shadow flex items-center justify-center transition-colors"
                                            data-testid="product-gallery-prev"
                                        >
                                            <ChevronLeft size={20} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => step(1)}
                                            aria-label="Next image"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-ink shadow flex items-center justify-center transition-colors"
                                            data-testid="product-gallery-next"
                                        >
                                            <ChevronRight size={20} />
                                        </button>
                                        <span className="absolute bottom-3 right-3 text-[11px] tracking-wider bg-black/55 text-white px-2 py-0.5 rounded-full" data-testid="product-gallery-count">
                                            {currentIdx + 1} / {allImages.length}
                                        </span>
                                    </>
                                )}
                            </div>
                            {Array.isArray(product.gallery) && product.gallery.length > 0 && (
                                <div className="flex gap-3 mt-3 overflow-x-auto pb-1" data-testid="product-gallery-thumbs">
                                    {allImages.map((src, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => setActiveImage(src)}
                                            className={`w-20 h-20 shrink-0 overflow-hidden border-2 transition-colors ${
                                                currentImage === src ? "border-blush" : "border-transparent opacity-70 hover:opacity-100 hover:border-[#EAE3DE]"
                                            }`}
                                            data-testid={`product-gallery-thumb-${i}`}
                                        >
                                            <SmartImage src={src} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {product.order_type === "enquiry" ? (
                            <div>
                                <p className="text-xs tracking-[0.3em] uppercase text-blush mb-3">{product.category}</p>
                                <h1 className="font-display font-bold text-4xl md:text-5xl text-ink mb-5 leading-none">
                                    {product.name}
                                </h1>
                                <p className="text-ink-soft leading-relaxed mb-2 whitespace-pre-line">
                                    {product.long_description}
                                </p>
                                {(() => {
                                    const info = productPriceInfo(product);
                                    return info ? (
                                        <p className="font-display text-2xl text-ink mb-3" data-testid="product-from-price">
                                            {info.from && <span className="text-xs tracking-[0.15em] uppercase text-ink-soft mr-1.5 align-middle">From</span>}
                                            ${info.amount.toFixed(0)}
                                        </p>
                                    ) : null;
                                })()}
                                {!(product.size_options && product.size_options.length > 0) && (
                                    <p className="text-blush text-sm mb-8">These are standard size cupcakes.</p>
                                )}
                                <div className="h-px bg-[#EAE3DE] mb-8" />
                                {product.category === "Vintage Heart Cakes" ? (
                                    <VintageHeartInquiryForm product={product} />
                                ) : product.size_options && product.size_options.length > 0 ? (
                                    <RectangularCakeInquiryForm product={product} />
                                ) : (
                                    <CupcakeInquiryForm product={product} />
                                )}
                            </div>
                        ) : (
                        <div>
                            <p className="text-xs tracking-[0.3em] uppercase text-blush mb-3">{product.category}</p>
                            <h1 className="font-display text-4xl md:text-5xl text-ink mb-3 leading-none">
                                {product.name}
                            </h1>
                            {(() => {
                                const basePrice = hasSizes ? (selectedSize?.price ?? 0) : product.price;
                                const perUnit = (hasSizes ? basePrice : effectiveUnitPrice(product.price, product.price_tiers, qty)) + surcharge;
                                const lineTotal = perUnit * qty;
                                const showsSavings = !hasSizes && perUnit - surcharge < Number(product.price);
                                return (
                                    <div className="mb-6">
                                        <p className="font-display text-3xl text-ink">
                                            ${lineTotal.toFixed(2)} <span className="text-sm text-ink-soft tracking-[0.2em] uppercase">AUD</span>
                                        </p>
                                        {qty > 1 && (
                                            <p className="text-xs text-ink-soft mt-1">
                                                ${perUnit.toFixed(2)} each × {qty}
                                            </p>
                                        )}
                                        {showsSavings && (
                                            <p className="text-xs text-blush tracking-[0.15em] uppercase mt-1">Bulk discount applied</p>
                                        )}
                                    </div>
                                );
                            })()}
                            {hasSizes && (
                                <div className="mb-6">
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-3">
                                        Pack Size
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {product.size_options.map((s) => (
                                            <button
                                                key={s.label}
                                                type="button"
                                                onClick={() => setSizeLabel(s.label)}
                                                className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${
                                                    sizeLabel === s.label
                                                        ? "bg-blush text-white border-blush"
                                                        : "bg-white border-[#EAE3DE] text-ink hover:border-blush"
                                                }`}
                                                data-testid={`size-${s.label}`}
                                            >
                                                {s.label} — ${Number(s.price).toFixed(0)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {!hasSizes && Array.isArray(product.price_tiers) && product.price_tiers.length > 0 && (
                                <div className="mb-6 p-4 bg-blush-pale/40 border border-blush/20">
                                    <p className="text-[10px] tracking-[0.3em] uppercase text-ink-soft mb-2">Buy more, save more</p>
                                    <ul className="space-y-1 text-sm">
                                        <li>1 = ${Number(product.price).toFixed(2)}</li>
                                        {product.price_tiers.map((t, i) => (
                                            <li key={i}>
                                                {t.qty} = ${Number(t.price).toFixed(2)}{" "}
                                                <span className="text-xs text-ink-soft">(${(Number(t.price) / Number(t.qty)).toFixed(2)} each)</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <p className="text-ink-soft leading-relaxed mb-8 whitespace-pre-line">
                                {product.long_description}
                            </p>

                            <div className="h-px bg-[#EAE3DE] mb-8" />

                            {Array.isArray(product.options) && product.options.length > 0 && (
                                <div className="mb-6">
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-3">
                                        Flavour / Option
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {product.options.map((opt) => (
                                            <button
                                                key={opt}
                                                onClick={() => setOption(opt)}
                                                className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${
                                                    option === opt
                                                        ? "bg-blush text-white border-blush"
                                                        : "bg-white border-[#EAE3DE] text-ink hover:border-blush"
                                                }`}
                                                data-testid={`option-${opt}`}
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {product.category === "Cupcakes" && (
                                <div className="mb-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={pickupChecked} onChange={(e) => setPickupChecked(e.target.checked)} data-testid="cupcake-cart-pickup" />
                                        <span className="text-xs tracking-[0.15em] uppercase text-ink">Pick up</span>
                                    </label>
                                    <p className="text-[11px] text-ink-soft italic mt-1.5">
                                        If delivery is required please email{" "}
                                        <a href="mailto:info@cakecentral.com.au" className="text-blush hover:underline">info@cakecentral.com.au</a>{" "}
                                        or SMS 0410 477 455 with the location and we will send you a quote.
                                    </p>
                                </div>
                            )}

                            {product.category === "Cookies" && (
                                <div className="mb-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={pickupChecked} onChange={(e) => setPickupChecked(e.target.checked)} data-testid="cookie-cart-pickup" />
                                        <span className="text-xs tracking-[0.15em] uppercase text-ink">Pick up</span>
                                    </label>
                                    <p className="text-[11px] text-ink-soft italic mt-1.5">
                                        If delivery is required please email{" "}
                                        <a href="mailto:info@cakecentral.com.au" className="text-blush hover:underline">info@cakecentral.com.au</a>{" "}
                                        or SMS 0410 477 455 with the location and what you're ordering, and we will send you a separate quote.
                                    </p>
                                </div>
                            )}

                            <div className="mb-6">
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-3">
                                    Quantity
                                </label>
                                <div className="inline-flex items-center border border-[#EAE3DE] bg-white">
                                    <button
                                        type="button"
                                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                                        className="px-3 py-2 text-ink hover:text-blush"
                                        aria-label="Decrease"
                                    >
                                        <Minus size={14} />
                                    </button>
                                    <span className="px-5 font-display text-lg min-w-[2rem] text-center">{qty}</span>
                                    <button
                                        type="button"
                                        onClick={() => setQty((q) => q + 1)}
                                        className="px-3 py-2 text-ink hover:text-blush"
                                        aria-label="Increase"
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <button onClick={addToCart} className="btn-pink btn-pink-outline" data-testid="add-to-cart-btn">
                                    <ShoppingBag size={14} /> Add to cart
                                </button>
                                <button onClick={buyNow} className="btn-pink" data-testid="buy-now-btn">
                                    Buy now
                                </button>
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
}
